import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, MonitorSmartphone, ShieldCheck, Sparkles } from "lucide-react";
import { cn } from "@/utils";
import { t } from "@/utils/i18n";
import { tHome } from "@/utils/i18n.home";
import { useLanguage } from "@/components/providers/language-provider";
import { usePackagePrices } from "@/hooks/usePackagePrices";

const PricingPage = () => {
  const { language } = useLanguage();
  const { display } = usePackagePrices();
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);

  const plans = [
    {
      name: t("home", "planAnnualName", language),
      info: t("home", "planAnnualInfo", language),
      price: display("year"),
      priceDetail: t("home", "planAnnualDetail", language),
      savings: null,
      features: [
        t("home", "planAnnualFeat1", language),
        t("home", "planAnnualFeat2", language),
        t("home", "planAnnualFeat3", language),
        t("home", "planAnnualFeat4", language),
        t("home", "planAnnualFeat5", language),
        t("home", "planAnnualFeat6", language),
      ],
      cta: t("home", "planAnnualCta", language),
      href: "/activation?plan=annual",
      highlight: false,
      recommended: false,
    },
    {
      name: t("home", "planGoldName", language),
      info: t("home", "planGoldInfo", language),
      price: display("lifetime"),
      priceDetail: t("home", "planGoldDetail", language),
      savings: t("home", "planGoldSavings", language),
      features: [
        t("home", "planGoldFeat1", language),
        t("home", "planGoldFeat2", language),
        t("home", "planGoldFeat3", language),
        t("home", "planGoldFeat4", language),
        t("home", "planGoldFeat5", language),
      ],
      cta: t("home", "planGoldCta", language),
      href: "/activation?plan=gold",
      highlight: true,
      recommended: true,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("pricing", "title", language)} - MoovaTV</title>
        <meta name="description" content={t("pricing", "subtitle", language)} />
      </Helmet>

      {/* One fold on desktop: compact heading, three plans side by side, disclaimer under them. */}
      <section className="px-4 sm:px-6 py-8 lg:py-6 lg:min-h-[calc(100svh-var(--moova-header-height))] lg:flex lg:items-center">
        <div className="w-full max-w-[1120px] mx-auto">
        <div className="text-center mb-7 md:mb-9">
          <h1 className="font-inter font-bold text-[28px] md:text-[38px] text-white tracking-[-0.03em] mb-2">
            {t("pricing", "title", language)}
          </h1>
          <p className="text-sm text-white/35 font-light max-w-md mx-auto whitespace-pre-line">
            {t("home", "pricingSub", language)}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch max-w-[820px] mx-auto">
          {plans.map((plan, i) => {
            const isHovered = hoveredPlan === i;
            return (
              <div
                key={plan.name}
                onMouseEnter={() => setHoveredPlan(i)}
                onMouseLeave={() => setHoveredPlan(null)}
                className={cn(
                  "relative rounded-[20px] border p-5 md:p-6 flex flex-col transition-all duration-500",
                  plan.highlight
                    ? "border-[#448AFF]/40 bg-[#448AFF]/[0.06] shadow-[0_0_40px_rgba(68,138,255,0.08)]"
                    : "border-white/[0.06] bg-white/[0.02]",
                  isHovered && !plan.highlight && "border-white/[0.12] bg-white/[0.04]",
                )}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#448AFF] text-white text-[10px] font-semibold px-3.5 py-1 rounded-full uppercase tracking-wider shadow-[0_0_20px_rgba(68,138,255,0.3)]">
                      {t("home", "planGoldBadge", language)}
                    </span>
                  </div>
                )}

                {plan.highlight && (
                  <div className="absolute -inset-[1px] rounded-[20px] bg-gradient-to-b from-[#448AFF]/20 to-transparent pointer-events-none" />
                )}

                <div className="relative flex flex-col flex-1">
                  <h3 className={cn("text-base font-semibold", plan.highlight ? "text-white" : "text-white/50")}>
                    {plan.name}
                  </h3>
                  <p className="text-xs text-white/25 mt-0.5 mb-4">{plan.info}</p>

                  <div className="flex items-baseline gap-2">
                    <span className={cn("font-inter font-black text-[34px] md:text-[40px] leading-none tracking-tight", plan.highlight ? "text-white" : "text-white/70")}>
                      {plan.price}
                    </span>
                    {plan.priceDetail && (
                      <span className="text-sm text-white/25">{plan.priceDetail}</span>
                    )}
                  </div>

                  <div className="h-6 mt-2 mb-3">
                    {plan.savings && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-[#448AFF]/10 border border-[#448AFF]/15 text-[10px] text-[#448AFF] font-medium">{plan.savings}</span>
                    )}
                  </div>

                  <div className={cn("h-[1px] mb-4", plan.highlight ? "bg-[#448AFF]/15" : "bg-white/[0.04]")} />

                  <ul className="space-y-2 mb-5 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="text-[13px] text-white/45 font-light flex items-start gap-2.5 leading-snug">
                        <Check className={cn("w-4 h-4 flex-shrink-0 mt-px", plan.highlight ? "text-[#448AFF]" : "text-white/15")} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={plan.href}
                    className={cn(
                      "block w-full text-center py-3 rounded-[12px] text-sm font-semibold transition-all duration-300",
                      plan.highlight
                        ? "bg-[#448AFF] hover:bg-[#448AFF]/90 text-white shadow-[0_0_24px_rgba(68,138,255,0.25)] hover:shadow-[0_0_40px_rgba(68,138,255,0.4)]"
                        : "bg-white/[0.06] text-white/50 hover:bg-white/[0.1] hover:text-white/70 border border-white/[0.04]"
                    )}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* The trial is not a plan: it starts on the TV, no card. These three facts replace the old "Free Trial" card. */}
        <ul className="flex flex-wrap justify-center gap-x-7 gap-y-2 mt-7 text-[13px] text-white/60">
          <li className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#448AFF]" />{tHome("prBen1", language)} · {t("home", "planFreeFeat3", language)}</li>
          <li className="inline-flex items-center gap-2"><MonitorSmartphone className="h-4 w-4 text-[#448AFF]" />{tHome("prBen3", language)}</li>
          <li className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#448AFF]" />{tHome("prBen2", language)}</li>
        </ul>
        <p className="text-center text-[11px] text-white/25 mt-5 max-w-lg mx-auto">
          {t("footer", "disclaimer", language)}
        </p>
        </div>
      </section>
    </>
  );
};

export default PricingPage;
